import { Linkedin, TrendingUp, UserPlus, Archive } from "lucide-react";
import type { Company } from "../api/HiringContactsApi";
import TechTagChip from "shared/TechTagChip";
import StatusNotePopover from "shared/StatusNotePopover";
import { useHiringContacts } from "../modal/HiringContactsContext";
import { WatchToggle, PriorityBadge } from "./ContactCard";

export const COMPANY_STATUS_CHIP: Record<string, { label: string; className: string }> = {
  new:          { label: "New",          className: "bg-blue-500/30 text-blue-200 border-blue-400/50" },
  following:    { label: "Following",    className: "bg-violet-500/30 text-violet-200 border-violet-400/50" },
  engaging:     { label: "Engaging",     className: "bg-fuchsia-500/30 text-fuchsia-200 border-fuchsia-400/50" },
  converted:    { label: "Converted",    className: "bg-emerald-500 text-emerald-950 border-emerald-400" },
  not_relevant: { label: "Not Relevant", className: "bg-zinc-600/50 text-zinc-300 border-zinc-500/50" },
  archived:     { label: "Archived",     className: "bg-zinc-800/70 text-zinc-400 border-zinc-600/50" },
};

interface Props {
  company: Company;
  onAssign?: () => void;
  /** Employee's own "My Companies" view only — a company is assigned to exactly one
   * TM there, so the whole card can open that single tracking modal directly. */
  onClick?: () => void;
  /** Admin view — a company can carry multiple independent TM assignments at once,
   * so each one gets its own clickable "name · status" chip instead of a single
   * card-level click; this opens the tracking modal for that specific assignment. */
  onSelectAssignment?: (assignmentId: number) => void;
}

export default function CompanyAssignmentCard({ company, onAssign, onClick, onSelectAssignment }: Props) {
  const { handleCompanyWatchToggle, handleCompanyPriorityChange, handleArchiveCompany, isAdmin } = useHiringContacts();

  // company.status is only present on items loaded from a /company-assignments
  // endpoint. Only shown for the employee's own single-assignment view — the admin
  // view shows status per-assignee in the chip row below instead, since a single
  // top badge can't represent multiple independent assignments.
  const status = company.status;
  const statusEntry = !isAdmin && status ? COMPANY_STATUS_CHIP[status] : null;

  const assignedEmployees = isAdmin ? company.assigned_employees ?? [] : [];

  return (
    <div
      onClick={onClick}
      className={`bg-surface/60 border border-border/60 rounded-2xl p-4 ${
        onClick ? "cursor-pointer hover:border-border transition-colors select-none" : ""
      }`}
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg border border-border bg-surface-muted flex items-center justify-center shrink-0">
          <span className="text-sm font-bold text-primary font-inter">
            {company.name.charAt(0).toUpperCase()}
          </span>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h3 className="text-sm font-semibold text-foreground font-inter leading-snug inline-flex items-center gap-1.5">
              {company.name}
              {company.linkedin_url && (
                <a
                  href={company.linkedin_url}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="text-sky-400"
                >
                  <Linkedin className="w-3.5 h-3.5" />
                </a>
              )}
            </h3>
            <div className="flex items-center gap-1.5 shrink-0" onClick={(e) => e.stopPropagation()}>
              <PriorityBadge priority={company.priority} onChange={(p) => handleCompanyPriorityChange(company.id, p)} />
              <WatchToggle
                isWatched={company.is_watched}
                onToggle={() => handleCompanyWatchToggle(company.id, !company.is_watched)}
                disabled={!company.linkedin_url}
              />
              {statusEntry && (
                <StatusNotePopover
                  note={company.assignment_notes}
                  fromLabel={company.assignment_from_status ? COMPANY_STATUS_CHIP[company.assignment_from_status]?.label : null}
                  toLabel={statusEntry.label}
                >
                  <span className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded border font-inter font-medium ${statusEntry.className}`}>
                    {statusEntry.label}
                  </span>
                </StatusNotePopover>
              )}
            </div>
          </div>

          <p className="text-xs text-muted-foreground font-inter mt-0.5">{company.domain}</p>

          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-[11px] text-muted-foreground font-inter">
            {company.industry && <span>{company.industry}</span>}
            {company.employee_count_range && <span>· {company.employee_count_range} employees</span>}
            {typeof company.num_jobs_last_30_days === "number" && company.num_jobs_last_30_days > 0 && (
              <span className="flex items-center gap-0.5 text-emerald-400">
                <TrendingUp className="w-3 h-3" />
                {company.num_jobs_last_30_days} jobs/30d
              </span>
            )}
          </div>

          {company.tech_tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {company.tech_tags.slice(0, 6).map((tag) => (
                <TechTagChip key={tag} label={tag} />
              ))}
            </div>
          )}

          {(assignedEmployees.length > 0 || onAssign) && (
            <div className="flex flex-wrap items-center gap-1.5 mt-2" onClick={(e) => e.stopPropagation()}>
              {assignedEmployees.map((a) => {
                const chip = COMPANY_STATUS_CHIP[a.status];
                return (
                  <button
                    key={a.assignment_id}
                    onClick={() => onSelectAssignment?.(a.assignment_id)}
                    className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full border font-inter font-medium transition-colors ${
                      onSelectAssignment ? "hover:brightness-110 cursor-pointer" : "cursor-default"
                    } ${chip?.className ?? "bg-surface-muted border-border text-foreground"}`}
                  >
                    {a.name}
                    <span className="opacity-70">· {chip?.label ?? a.status}</span>
                  </button>
                );
              })}
              {onAssign && (
                <>
                  <button
                    onClick={() => handleArchiveCompany(company.id)}
                    title="Archiving also archives every contact under this company"
                    className="ml-auto flex items-center gap-1 text-[11px] text-muted-foreground hover:text-destructive bg-surface-muted hover:bg-destructive/10 border border-border hover:border-destructive/30 rounded-lg px-2 py-1 transition-colors font-inter"
                  >
                    <Archive className="w-3 h-3" />
                    Archive
                  </button>
                  <button
                    onClick={onAssign}
                    className="flex items-center gap-1 text-[11px] text-primary hover:text-primary-foreground bg-primary/20 hover:bg-primary border border-primary/30 hover:border-primary rounded-lg px-2 py-1 transition-colors font-inter"
                  >
                    <UserPlus className="w-3 h-3" />
                    Assign
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
